import { useEffect, useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import type { ApiError } from '../../../lib/errors';
import { rpaRepository as repo } from '../../../lib/repositories/rpa.repository';
import type {
  BaoCaoLoi,
  DanhSachSuCo,
  DoiChieuDuLieu,
  DongThoiGianLuot,
  KetQuaCao,
  KhoaLuotDuLieu,
  LoaiDuLieu,
  LogRobot,
  ThongKeDuLieu,
  TrangDuLieu,
  TrangLichSu,
  TruyVanLichSu,
} from '../types';
import { KHOA_RPA, thuLai } from './useRpa';

// ─────────────── Lịch sử lượt chạy ───────────────

/** Phân trang, tìm phía máy chủ; giữ trang cũ trong lúc tải trang mới để bảng không nháy về Skeleton. */
export function useLichSu(q: TruyVanLichSu, bat = true) {
  return useQuery<TrangLichSu, ApiError>({
    queryKey: KHOA_RPA.trangLichSu(q),
    queryFn: () => repo.lichSu(q),
    enabled: bat,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    retry: thuLai,
  });
}

/** Báo cáo lỗi Robot gửi về theo InstanceId (`null` khi chưa có). */
export function useBaoCaoInstance(instanceId: string) {
  return useQuery<BaoCaoLoi | null, ApiError>({
    queryKey: KHOA_RPA.baoCaoInstance(instanceId),
    queryFn: () => repo.baoCaoInstance(instanceId),
    enabled: instanceId !== '',
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Báo cáo lỗi theo mã lượt Native `RUN-…` (lượt không có InstanceId, như lượt FOXAI gửi lệnh rồi hỏng). */
export function useBaoCaoLuot(runCode: string) {
  return useQuery<BaoCaoLoi | null, ApiError>({
    queryKey: KHOA_RPA.baoCaoLuot(runCode),
    queryFn: () => repo.baoCaoLuot(runCode),
    enabled: runCode !== '',
    staleTime: 30_000,
    retry: thuLai,
  });
}

export function useLogInstance(instanceId: string) {
  return useQuery<LogRobot | null, ApiError>({
    queryKey: KHOA_RPA.logInstance(instanceId),
    queryFn: () => repo.logInstance(instanceId),
    enabled: instanceId !== '',
    staleTime: 30_000,
    retry: thuLai,
  });
}

export function useDongThoiGian(runCode: string) {
  return useQuery<DongThoiGianLuot, ApiError>({
    queryKey: KHOA_RPA.dongThoiGian(runCode),
    queryFn: () => repo.dongThoiGian(runCode),
    enabled: runCode !== '',
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Tệp bằng chứng tải về kèm token; log đọc sẵn ra chữ. Giữ 5 phút: xem lại video không tải lại 100 MB. */
export function useTepBangChung(executionId: string, fileId: string, laLog: boolean) {
  const q = useQuery<{ blob: Blob; chu: string | null }, ApiError>({
    queryKey: KHOA_RPA.tepBangChung(executionId, fileId),
    queryFn: async () => {
      const blob = await repo.tepBangChung(executionId, fileId);
      return { blob, chu: laLog ? await blob.text() : null };
    },
    staleTime: 5 * 60_000,
    gcTime: 5 * 60_000,
    retry: thuLai,
  });
  const blob = q.data?.blob;
  const diaChi = useMemo(() => (blob ? URL.createObjectURL(blob) : undefined), [blob]);
  // Thu hồi URL tạm khi tệp đổi hoặc hộp đóng — dọn tài nguyên của trình duyệt, đúng việc của effect.
  useEffect(() => () => {
    if (diaChi) URL.revokeObjectURL(diaChi);
  }, [diaChi]);
  return { ...q, diaChi };
}

// ─────────────── Dữ liệu thực thi ───────────────

const maCua = (k: KhoaLuotDuLieu) => k.instanceId || k.runId || '';

export function useThongKeDuLieu(k: KhoaLuotDuLieu) {
  return useQuery<ThongKeDuLieu, ApiError>({
    queryKey: [...KHOA_RPA.duLieu(maCua(k)), 'thong-ke'],
    queryFn: () => repo.thongKeDuLieu(k),
    enabled: maCua(k) !== '',
    staleTime: 30_000,
    retry: thuLai,
  });
}

export function useDongDuLieu(k: KhoaLuotDuLieu, q: { kind: LoaiDuLieu; search: string; page: number; pageSize: number }) {
  return useQuery<TrangDuLieu, ApiError>({
    queryKey: [...KHOA_RPA.duLieu(maCua(k)), 'dong', q],
    queryFn: () => repo.dongDuLieu({ ...k, ...q }),
    enabled: maCua(k) !== '',
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: thuLai,
  });
}

/** Xuất tệp: toàn bộ dòng khớp tìm kiếm (), một lần hỏi theo yêu cầu, không cache. */
export function useXuatDuLieu() {
  return useMutation<TrangDuLieu, ApiError, KhoaLuotDuLieu & { kind: LoaiDuLieu; search: string }>({
    mutationFn: (q) => repo.dongDuLieu({ ...q, page: 1, pageSize: 100, all: true }),
  });
}

/** Đổi cột ghép giữ kết quả cũ trong lúc tải: ô chọn cột không biến mất giữa chừng. */
export function useDoiChieu(k: KhoaLuotDuLieu, keys: readonly string[]) {
  return useQuery<DoiChieuDuLieu, ApiError>({
    queryKey: [...KHOA_RPA.duLieu(maCua(k)), 'doi-chieu', keys],
    queryFn: () => repo.doiChieu(k, keys),
    enabled: maCua(k) !== '',
    staleTime: 30_000,
    placeholderData: (cu, truyVanCu) => (truyVanCu?.queryKey[2] === maCua(k) ? cu : undefined),
    retry: thuLai,
  });
}

export function useKetQuaCao(executionId: string) {
  return useQuery<KetQuaCao[], ApiError>({
    queryKey: [...KHOA_RPA.duLieu(executionId), 'ket-qua-cao'],
    queryFn: () => repo.ketQuaCao({ executionId }),
    enabled: executionId !== '',
    staleTime: 30_000,
    retry: thuLai,
  });
}

// ─────────────── Sự cố ───────────────

export function useSuCo(bat = true) {
  return useQuery<DanhSachSuCo, ApiError>({
    queryKey: KHOA_RPA.suCo,
    queryFn: () => repo.suCo(),
    enabled: bat,
    staleTime: 60_000,
    retry: thuLai,
  });
}
